import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Logo from "@/components/Logo";
import {
  ArrowRight,
  ShieldCheck,
  CheckCircle,
  Vault,
  Calendar,
  ChatCircleDots,
  FilePdf,
  WhatsappLogo,
  ArrowLeft,
  CurrencyInr,
  ArrowUUpLeft,
  Sparkle,
} from "@phosphor-icons/react";

const WHATSAPP_URL = "https://wa.me/917709773338?text=" + encodeURIComponent(
  "Hi Profitink — I'd like to book the ₹8,000 diagnostic audit. My business is: "
);

const CHECKLIST = [
  "GSTR-1 and GSTR-3B filings — last 4 quarters",
  "TDS returns — Form 26Q / 24Q",
  "Latest ITR with computation",
  "P&L and Balance Sheet (or Tally / Zoho Books export)",
  "Bank statements — any one month is enough",
  "Any GST / Income Tax notices received",
  "Cap table / shareholding pattern (if company / LLP)",
];

const DIAGNOSTIC_POINTS = [
  ["01", "GST Input Credit reconciliation", "Unclaimed ITC, mismatches"],
  ["02", "HSN / SAC classification", "Wrong rates being charged"],
  ["03", "TDS compliance", "Late deductions, wrong sections (194J vs 194C etc.)"],
  ["04", "Presumptive taxation eligibility", "Sec 44AD / 44ADA opportunity"],
  ["05", "Depreciation & fixed assets", "Missed additions, block classification"],
  ["06", "Related party transactions", "Sec 40A(2) exposure"],
  ["07", "Director remuneration / dividend", "Optimal salary vs profit split"],
  ["08", "Entity structure fit", "Prop → LLP → Pvt Ltd trigger points"],
  ["09", "Advance tax planning", "Interest u/s 234B / 234C avoidance"],
  ["10", "Notices & pending litigation", "Response strategy"],
];

const STEPS = [
  { icon: ChatCircleDots, num: "01", title: "Kickoff Call", meta: "15 min · WhatsApp video or Google Meet",
    body: "We understand your business model, entity type and revenue scale. Identify pain points — late filings, TDS notice, GSTR-3B mismatch — and confirm the scope." },
  { icon: Vault, num: "02", title: "Secure Document Share", meta: "You share · 24-48 hrs",
    body: "Upload the last four quarters to our encrypted Profitink Document Vault. Mutual NDA signed before anything moves. Delete-all button on your side after the audit." },
  { icon: Sparkle, num: "03", title: "10-Point Deep Diagnostic", meta: "2 hrs of expert time · our side",
    body: "Our team runs a ten-point diagnostic across GST, TDS, income tax, structure, cash flow and open notices — finding the leaks and the levers." },
  { icon: FilePdf, num: "04", title: "PDF Report + Debrief Call", meta: "30 min walkthrough",
    body: "You receive a written diagnostic PDF via email and client portal, followed by a 30-minute debrief. Yours to keep — no obligation to sign a retainer." },
];

const Audit = () => {
  return (
    <div className="marketing-scope min-h-screen bg-[#06120E] text-[#F9F6F0] font-sans overflow-x-hidden">
      {/* NAV */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-[#06120E]/70 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="audit-nav-home">
            <Logo size={34} tone="gold" wordmarkClassName="text-xl text-[#F9F6F0]" />
          </Link>
          <Link
            to="/"
            className="text-sm text-white/70 hover:text-[#D4AF37] transition-colors flex items-center gap-2"
            data-testid="audit-back-home"
          >
            <ArrowLeft size={14} /> Back to Home
          </Link>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0 opacity-25 noise-overlay pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 grid grid-cols-12 gap-10 items-start">
          <div className="col-span-12 lg:col-span-8">
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-6"
            >
              The Profitink Diagnostic · Free of assumptions
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
              className="font-serif-display text-5xl md:text-7xl leading-[1.02] tracking-tight"
            >
              A written review of<br />
              <span className="italic text-[#D4AF37]">your last four quarters.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-8 text-lg text-white/70 max-w-2xl leading-relaxed"
            >
              A two-hour, ten-point diagnostic on your books — tax leaks, compliance gaps,
              structural fit and cash-flow observations — delivered as a PDF within 5 working days.
              Zero sales pitch.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <a
                href={WHATSAPP_URL}
                target="_blank" rel="noopener noreferrer"
                className="group brand-gold-bg px-8 py-4 flex items-center gap-3 text-sm tracking-wider uppercase font-semibold hover:opacity-90"
                data-testid="audit-hero-cta-whatsapp"
              >
                Book the diagnostic <ArrowRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform" />
              </a>
              <a href="#pricing" className="px-8 py-4 border border-white/20 text-white/80 hover:border-[#D4AF37] hover:text-[#D4AF37] text-sm tracking-wider uppercase transition-colors" data-testid="audit-hero-cta-pricing">
                See how the ₹8,000 works
              </a>
            </motion.div>
          </div>

          {/* pricing pill */}
          <div className="col-span-12 lg:col-span-4">
            <div className="border border-[#D4AF37] bg-[#0A1B14] p-8 relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#D4AF37]/10 blur-2xl" />
              <p className="text-[#D4AF37] text-[11px] tracking-[0.34em] uppercase">Diagnostic Fee</p>
              <div className="flex items-baseline gap-2 mt-4">
                <span className="font-mono-data text-6xl text-[#D4AF37]">₹8,000</span>
                <span className="text-white/50 text-sm">/ one-time</span>
              </div>
              <p className="mt-5 text-white/70 text-sm leading-relaxed">
                A refundable commitment fee — because our best work happens when both sides are serious.
              </p>
              <ul className="mt-6 space-y-3 text-sm">
                <li className="flex items-start gap-2">
                  <ArrowUUpLeft size={18} weight="duotone" className="text-[#D4AF37] shrink-0 mt-0.5" />
                  <span><span className="text-[#D4AF37]">Adjusted</span> against your first month if you sign a retainer</span>
                </li>
                <li className="flex items-start gap-2">
                  <CurrencyInr size={18} weight="duotone" className="text-[#D4AF37] shrink-0 mt-0.5" />
                  <span><span className="text-[#D4AF37]">Refunded</span> in full if the report doesn&rsquo;t deliver value</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="border-t border-white/5 bg-[#0A1B14]/30">
        <div className="max-w-7xl mx-auto px-6 py-20 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-5">
            <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">What you get</p>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight">A written diagnostic report.</h2>
            <p className="text-white/70 mt-5 leading-relaxed max-w-lg">
              Not a sales deck. Not a spreadsheet dump. A crisp PDF — findings,
              exposures, and what to fix in what order.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-6 lg:col-start-7 space-y-4">
            {[
              ["Tax leaks & missed deductions", "Where you&rsquo;re overpaying — and by how much."],
              ["Compliance gaps", "GST, TDS and ITR filings that need cleanup."],
              ["Entity & structure review", "Is your current setup quietly costing you?"],
              ["Working-capital & cash-flow", "Observations from your bank statements."],
              ["A 3-6 month clean-up roadmap", "Prioritised. Actionable. Realistic."],
            ].map(([t, d], i) => (
              <div key={i} className="flex items-start gap-4 border border-white/10 p-5">
                <CheckCircle size={22} weight="duotone" className="text-[#D4AF37] shrink-0 mt-0.5" />
                <div>
                  <div className="font-serif-display text-xl">{t}</div>
                  <div className="text-white/60 text-sm mt-1" dangerouslySetInnerHTML={{__html: d}} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4-STEP PROCESS */}
      <section className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="grid grid-cols-12 gap-8 mb-16">
            <div className="col-span-12 md:col-span-5">
              <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">The process</p>
              <h2 className="font-serif-display text-4xl md:text-5xl leading-tight">
                Four steps.<br /><span className="italic">One clean week.</span>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-6 md:col-start-7 text-white/70 leading-relaxed self-end">
              Total elapsed time: about a week. Your effort: roughly 45 minutes.
              Everything else is on us.
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {STEPS.map((s) => (
              <div key={s.num} className="border border-white/10 p-8 hover:border-[#D4AF37]/50 transition-colors">
                <div className="flex items-start justify-between">
                  <s.icon size={28} weight="duotone" className="text-[#D4AF37]" />
                  <span className="font-mono-data text-[#D4AF37]/70 text-lg">{s.num}</span>
                </div>
                <h3 className="font-serif-display text-2xl mt-6">{s.title}</h3>
                <p className="text-white/50 text-xs uppercase tracking-widest mt-2">{s.meta}</p>
                <p className="text-white/70 text-sm leading-relaxed mt-4">{s.body}</p>
              </div>
            ))}
          </div>

          {/* Checklist card */}
          <div className="mt-10 border border-white/10 bg-[#0A1B14]/50 p-8">
            <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">Checklist for step 2</p>
            <h3 className="font-serif-display text-2xl mb-6">What we&rsquo;ll need &mdash; last four quarters</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
              {CHECKLIST.map((c) => (
                <div key={c} className="flex items-start gap-3 text-white/80 text-sm">
                  <CheckCircle size={18} weight="duotone" className="text-[#D4AF37] mt-0.5 shrink-0" /> {c}
                </div>
              ))}
            </div>
            <p className="text-white/50 text-xs mt-6 italic">
              Sole proprietors: just the ITR + last 3 bank statements is enough.
            </p>
          </div>
        </div>
      </section>

      {/* 10-POINT DIAGNOSTIC */}
      <section className="border-t border-white/5 bg-[#0A1B14]/30">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="text-center mb-16">
            <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">The diagnostic</p>
            <h2 className="font-serif-display text-4xl md:text-5xl">Ten places we look. Every time.</h2>
          </div>
          <div className="border-t border-l border-white/10">
            <div className="grid grid-cols-12 border-b border-white/10 bg-[#06120E] text-[10px] uppercase tracking-widest text-white/50">
              <div className="col-span-1 p-4 border-r border-white/10">#</div>
              <div className="col-span-4 p-4 border-r border-white/10">Check</div>
              <div className="col-span-7 p-4">What we look for</div>
            </div>
            {DIAGNOSTIC_POINTS.map(([n, c, w]) => (
              <div key={n} className="grid grid-cols-12 border-b border-white/10 hover:bg-[#0A1B14] transition-colors">
                <div className="col-span-1 p-5 border-r border-white/10 font-mono-data text-[#D4AF37]">{n}</div>
                <div className="col-span-4 p-5 border-r border-white/10 font-serif-display text-lg">{c}</div>
                <div className="col-span-7 p-5 text-white/70 text-sm self-center">{w}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING & GUARANTEE */}
      <section id="pricing" className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-24 grid grid-cols-12 gap-10 items-start">
          <div className="col-span-12 lg:col-span-6">
            <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">Pricing &amp; guarantee</p>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight">
              ₹8,000 &mdash;<br /><span className="italic">skin in the game, both sides.</span>
            </h2>
            <p className="text-white/70 mt-6 leading-relaxed max-w-lg">
              Free audits attract tyre-kickers. We&rsquo;d rather work with founders who are serious
              enough to put a small stake down &mdash; and we&rsquo;re confident enough to stand
              behind the report with a full refund if you don&rsquo;t see the value.
            </p>
          </div>

          <div className="col-span-12 lg:col-span-6 space-y-4">
            <div className="border border-[#D4AF37] p-8 bg-[#0A1B14]">
              <div className="flex items-baseline gap-3">
                <span className="font-mono-data text-6xl text-[#D4AF37]">₹8,000</span>
                <span className="text-white/50">/ one-time diagnostic</span>
              </div>
              <div className="mt-8 space-y-5">
                <div className="flex items-start gap-4">
                  <ArrowUUpLeft size={22} weight="duotone" className="text-[#D4AF37] shrink-0 mt-1" />
                  <div>
                    <div className="font-serif-display text-xl">Fully adjustable</div>
                    <div className="text-white/60 text-sm mt-1">
                      If you sign a retainer with us, the ₹8,000 is 100% adjusted against your first month&rsquo;s fee.
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <ShieldCheck size={22} weight="duotone" className="text-[#D4AF37] shrink-0 mt-1" />
                  <div>
                    <div className="font-serif-display text-xl">Value-back guarantee</div>
                    <div className="text-white/60 text-sm mt-1">
                      If the diagnostic doesn&rsquo;t deliver value in your view, we&rsquo;ll refund the ₹8,000 in full &mdash; no questions asked.
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Vault size={22} weight="duotone" className="text-[#D4AF37] shrink-0 mt-1" />
                  <div>
                    <div className="font-serif-display text-xl">NDA-protected</div>
                    <div className="text-white/60 text-sm mt-1">
                      Mutual NDA before you share any documents. ICAI-regulated. Encrypted vault.
                    </div>
                  </div>
                </div>
              </div>
              <a
                href={WHATSAPP_URL}
                target="_blank" rel="noopener noreferrer"
                data-testid="audit-pricing-cta"
                className="group mt-10 w-full brand-gold-bg py-4 flex items-center justify-center gap-3 text-sm tracking-widest uppercase font-semibold hover:opacity-90"
              >
                <WhatsappLogo size={18} weight="fill" /> Book the diagnostic
                <ArrowRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* TIMELINE STRIP */}
      <section className="border-t border-white/5 bg-[#0A1B14]/40">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-8">Timeline</p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              ["Day 0", "Kickoff call"],
              ["Day 1-2", "You share documents"],
              ["Day 3-5", "We run the diagnostic"],
              ["Day 5-7", "Report delivered + debrief"],
            ].map(([d, w]) => (
              <div key={d} className="flex items-start gap-4">
                <Calendar size={22} weight="duotone" className="text-[#D4AF37] shrink-0 mt-1" />
                <div>
                  <div className="font-mono-data text-[#D4AF37] text-sm">{d}</div>
                  <div className="font-serif-display text-xl mt-1">{w}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">Ready when you are</p>
          <h2 className="font-serif-display text-4xl md:text-5xl leading-tight">
            Message us with <span className="italic text-[#D4AF37]">one line.</span>
          </h2>
          <div className="mt-8 font-mono-data text-white/80 text-lg bg-[#0A1B14] border border-white/10 p-6 max-w-2xl mx-auto">
            &ldquo;Hi Profitink — I&rsquo;d like the ₹8,000 diagnostic. My business is [name].&rdquo;
          </div>
          <a
            href={WHATSAPP_URL}
            target="_blank" rel="noopener noreferrer"
            data-testid="audit-final-cta"
            className="group inline-flex items-center gap-3 mt-10 bg-[#25D366] hover:bg-[#1DA851] text-white px-8 py-4 text-sm tracking-widest uppercase font-semibold transition-colors"
          >
            <WhatsappLogo size={18} weight="fill" /> WhatsApp +91 77097 73338
            <ArrowRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </section>

      <footer className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-white/50">
          <p>© {new Date().getFullYear()} Profitink — Chartered Accountants</p>
          <p className="font-mono-data text-xs">MADE IN INDIA · FILED ON TIME</p>
        </div>
      </footer>

      {/* Floating WhatsApp */}
      <a
        href={WHATSAPP_URL}
        target="_blank" rel="noopener noreferrer"
        data-testid="audit-floating-whatsapp"
        aria-label="Chat on WhatsApp"
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 bg-[#25D366] hover:bg-[#1DA851] text-white px-4 py-3 shadow-lg shadow-black/40 transition-colors"
      >
        <WhatsappLogo size={22} weight="fill" />
        <span className="hidden sm:inline text-sm font-semibold tracking-wide">WhatsApp us</span>
      </a>
    </div>
  );
};

export default Audit;
